import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { PageTitle } from '../../../../../../../_metronic/layout/core'
import { KTIcon } from '../../../../../../../_metronic/helpers'
import { Table } from '../../../../../../../components/Table/Table'
import { TableHead } from '../../../../../../../components/Table/TableHead/TableHead'
import { TableBody } from '../../../../../../../components/Table/TableBody/TableBody'
import { LoaderForTable } from '../../../../../../../components/Loader'
import {
  createCommissionRule,
  deleteCommissionRule,
  getCommissionRulesList,
  updateCommissionRule,
} from '../../core/api'
import { COMMISSION_RULES_BREADCRUMBS } from '../../core/consts'
import { CommissionRulesQuery } from '../../core/query.types'
import { DEFAULT_COMMISSION_RULES_QUERY } from '../../core/query.defaults'
import { ICommissionRule } from '../../core/response.types'
import {
  CreateCommissionRulePayload,
  UpdateCommissionRulePayload,
} from '../../core/requests.types'
import CommissionRulesRow from './CommissionRulesRow'
import CommissionRulesFilters from './CommissionRulesFilters'
import CommissionRulesCursorNav from './CommissionRulesCursorNav'
import CommissionRuleModal from '../CommissionRuleModal/CommissionRuleModal'
import CommissionRuleAuditModal from '../CommissionRuleAuditModal/CommissionRuleAuditModal'
import CommissionPreviewModal from '../CommissionPreviewModal/CommissionPreviewModal'

type EditingState = ICommissionRule | null | undefined // undefined = closed, null = create, ICommissionRule = edit

const CommissionRulesList: React.FC = () => {
  const [filters, setFilters]         = useState<CommissionRulesQuery>(DEFAULT_COMMISSION_RULES_QUERY)
  const [items, setItems]             = useState<ICommissionRule[]>([])
  const [loading, setLoading]         = useState(false)
  const [stack, setStack]             = useState<string[]>([])
  const [nextCursor, setNextCursor]   = useState<string | null>(null)
  const [editing, setEditing]         = useState<EditingState>(undefined)
  const [auditRuleId, setAuditRuleId] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  const load = useCallback(async (q: CommissionRulesQuery) => {
    setLoading(true)
    try {
      const resp = await getCommissionRulesList(q)
      if (!resp.data?.success) {
        setItems([])
        setNextCursor(null)
        return
      }
      setItems(resp.data.data ?? [])
      setNextCursor(resp.data.nextCursor ?? null)
    } catch (e) {
      setItems([])
      setNextCursor(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(filters) }, [filters, load])

  const onChangeFilters = useCallback((next: CommissionRulesQuery) => {
    setStack([])
    setFilters({ ...next, cursor: undefined })
  }, [])

  const onNext = useCallback(() => {
    if (!nextCursor) return
    setStack((s) => [...s, filters.cursor ?? ''])
    setFilters({ ...filters, cursor: nextCursor })
  }, [filters, nextCursor])

  const onPrev = useCallback(() => {
    if (stack.length === 0) return
    const prev = [...stack]
    const last = prev.pop() ?? ''
    setStack(prev)
    setFilters({ ...filters, cursor: last || undefined })
  }, [stack, filters])

  const openCreate = () => setEditing(null)
  const openEdit   = (r: ICommissionRule) => setEditing(r)
  const closeModal = () => setEditing(undefined)
  const openAudit  = (r: ICommissionRule) => setAuditRuleId(r.id)
  const closeAudit = () => setAuditRuleId(null)

  const handleSubmit = async (
    payload: CreateCommissionRulePayload | UpdateCommissionRulePayload,
    mode: 'create' | 'edit',
  ) => {
    if (mode === 'create') {
      const resp = await createCommissionRule(payload as CreateCommissionRulePayload)
      if (resp.data?.success) {
        await Swal.fire({ icon: 'success', title: 'Правило создано', timer: 1500, showConfirmButton: false })
        closeModal()
        load(filters)
        return
      }
      Swal.fire('Ошибка', resp.data?.errors?.general_error ?? 'Не удалось сохранить', 'error')
      return
    }
    if (mode === 'edit' && editing) {
      const resp = await updateCommissionRule(editing.id, payload as UpdateCommissionRulePayload)
      if (resp.data?.success) {
        await Swal.fire({ icon: 'success', title: 'Правило обновлено', timer: 1500, showConfirmButton: false })
        closeModal()
        load(filters)
        return
      }
      Swal.fire('Ошибка', resp.data?.errors?.general_error ?? 'Не удалось сохранить', 'error')
    }
  }

  const handleDelete = async (r: ICommissionRule) => {
    const c = await Swal.fire({
      title: 'Удалить правило?',
      text: 'Действие нельзя отменить',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Удалить',
      cancelButtonText: 'Отмена',
    })
    if (!c.isConfirmed) return
    try {
      await deleteCommissionRule(r.id)
      await Swal.fire({ icon: 'success', title: 'Правило удалено', timer: 1500, showConfirmButton: false })
      load(filters)
    } catch (e: any) {
      Swal.fire('Ошибка', e?.response?.data?.errors?.general_error ?? 'Не удалось удалить', 'error')
    }
  }

  const breadcrumbs = useMemo(() => COMMISSION_RULES_BREADCRUMBS, [])

  return (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={breadcrumbs}>Комиссии</PageTitle>
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Правила комиссий</span>
          </h3>
          <div className='card-toolbar gap-2'>
            <button type='button' className='btn btn-sm btn-light-primary' onClick={() => setPreviewOpen(true)}>
              <KTIcon iconName='calculator' className='fs-2' /> Проверить комиссию
            </button>
            <button type='button' className='btn btn-sm btn-primary' onClick={openCreate}>
              <KTIcon iconName='plus' className='fs-2' /> Создать правило
            </button>
          </div>
        </div>
      </div>

      <CommissionRulesFilters onChange={onChangeFilters} />

      <div className='card'>
        <div className='card-body py-3'>
          <Table>
            <TableHead>
              <tr>
                <th className='min-w-120px'>Направление</th>
                <th className='min-w-60px'>Валюта</th>
                <th className='min-w-200px'>Область</th>
                <th className='min-w-120px'>Платёжный метод</th>
                <th className='min-w-60px' title='Процентная ставка со знаком: «+» — комиссия с мерчанта, «−» — ребейт (PS платит мерчанту)'>
                  Процент*
                </th>
                <th className='min-w-100px'>Фикс</th>
                <th className='min-w-120px'>Комиссия мин / макс</th>
                <th className='min-w-150px'>Диапазон суммы</th>
                <th className='min-w-160px'>Бейджи</th>
                <th className='min-w-140px'>Создано</th>
                <th className='min-w-140px'>Изменено</th>
                <th className='min-w-200px text-end'>Действия</th>
              </tr>
            </TableHead>
            <TableBody>
              {!loading && items.length > 0
                ? items.map((rule) => (
                    <CommissionRulesRow
                      key={rule.id}
                      rule={rule}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onAudit={openAudit}
                    />
                  ))
                : <LoaderForTable isLoading={loading} colSpan={11} />}
            </TableBody>
          </Table>
          <CommissionRulesCursorNav
            hasPrev={stack.length > 0}
            hasNext={!!nextCursor}
            onPrev={onPrev}
            onNext={onNext}
            loading={loading}
          />
        </div>
      </div>

      {editing !== undefined && (
        <CommissionRuleModal
          show
          rule={editing}
          onClose={closeModal}
          onSubmit={handleSubmit}
        />
      )}
      <CommissionRuleAuditModal
        show={!!auditRuleId}
        ruleId={auditRuleId}
        onClose={closeAudit}
      />
      <CommissionPreviewModal
        show={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  )
}

export default CommissionRulesList
